import { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  StaticPageLayout,
  Section,
  PageHero,
  CtaBanner,
  Callout,
  Card,
  GroupLabel,
  P,
  B,
} from 'app/components/StaticPages';

interface MissionCardProps {
  /** Palette path for the accent bar, e.g. 'staticPages.orange' */
  barColor: string;
  title: string;
  children: ReactNode;
}

const MissionCard: FC<MissionCardProps> = ({ barColor, title, children }) => (
  <Card
    sx={{
      typography: 'body1',
      borderColor: 'staticPages.border.cardAlt',
      px: { xs: 3, md: 5 },
      py: { xs: 3.5, md: 5 },
      boxShadow: theme => theme.palette.staticPages.shadow.lg,
      lineHeight: 1.7,
      color: 'staticPages.text.secondary',
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 6,
        borderRadius: '999px',
        bgcolor: barColor,
        mb: 3,
      }}
    />
    <Typography variant="h2" sx={{ mb: 2, color: 'staticPages.navy' }}>
      {title}
    </Typography>
    {children}
  </Card>
);

const AboutUs = () => (
  <StaticPageLayout
    title="Kingdom Strollers | Save 50% on Orlando Stroller Rentals"
    metaDescription="Kingdom Strollers is the first choice for Orlando stroller rentals throughout all of Central Florida. Save up to 50% with us compared to Disney fees!"
  >
    <PageHero
      eyebrow="About Kingdom Strollers"
      title="Orlando's Premier Stroller Rental"
      subtitle="Our goal is to be your first choice, every time."
      subtitleMaxWidth={620}
    />

    {/* Our Story */}
    <Section innerSx={{ py: { xs: 5, md: 8 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr .95fr' },
          gap: { xs: 4, md: 7 },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            typography: 'body1',
            lineHeight: 1.7,
            color: 'staticPages.text.story',
          }}
        >
          <GroupLabel sx={{ fontWeight: 600, mb: 2 }}>Our Story</GroupLabel>
          <P sx={{ mb: 2.5 }}>
            Thank you for taking the time to learn a little bit more about us.
            We know you have many choices when it comes to renting a stroller
            for your Orlando vacation; our goal is to be your first choice,
            every time.
          </P>
          <P sx={{ mb: 3.5 }}>
            This small, family-owned business started with my goal of being able
            to spend more time with my family. After 10 years as a restaurant
            General Manager, I wanted to find a way to be able to enjoy the
            early years of my son as he grew. I quit my career just four months
            after starting this business. I thank the Lord for his guidance
            every day and for the blessing I have in being the steward over what
            he has provided.
          </P>
          <Callout
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              px: 3,
              py: 2,
            }}
          >
            <Box>
              <Typography variant="h3" sx={{ color: 'staticPages.navy' }}>
                Nelson &amp; Gaby
              </Typography>
              <Typography
                variant="eyebrow"
                sx={{ color: 'staticPages.text.light', fontWeight: 500 }}
              >
                Owners
              </Typography>
            </Box>
          </Callout>
        </Box>

        {/* Right column intentionally empty — reserved for a future owner photo */}
        <Box />
      </Box>
    </Section>

    {/* Mission & Vision */}
    <Section
      bandSx={{
        bgcolor: 'staticPages.bg.band',
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'staticPages.border.band',
      }}
      innerSx={{ py: { xs: 5, md: 8 } }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3.5,
        }}
      >
        <MissionCard barColor="staticPages.orange" title="Our Mission">
          <P sx={{ mb: 2 }}>
            At <B navy>Kingdom Strollers</B>, our mission is to provide families
            with a seamless, stress-free experience through premium stroller
            rentals and exceptional service. We are dedicated to supporting
            families on their Orlando vacation journeys by offering strollers
            that are impeccably clean, comfortable, and reliable.
          </P>
          <P sx={{ mb: 2 }}>
            Our team takes great pride in being hands-on and personally involved
            — because every family deserves care that feels genuine. Our
            managers are always available to answer your calls, ensuring your
            needs are met with warmth, respect, and attention to detail.
          </P>
          <P sx={{ color: 'staticPages.navy', fontWeight: 600 }}>
            At Kingdom Strollers, every family matters — and putting our
            customers first isn&apos;t just what we do, it&apos;s who we are.
          </P>
        </MissionCard>

        <MissionCard barColor="staticPages.blue" title="Our Vision">
          <P>
            We envision a world where every family&apos;s journey is enhanced by
            our reliable and comfortable stroller rentals. We aim to be the most
            trusted family service brand for vacationing parents, creating
            joyful, stress-free experiences that allow parents to focus on what
            truly matters: making memories with their children. Thank you for
            taking the time to learn a little more about us—we&apos;re glad
            you&apos;re here!
          </P>
        </MissionCard>
      </Box>
    </Section>

    {/* Closing banner */}
    <Section innerSx={{ py: { xs: 5, md: 8 } }}>
      <CtaBanner
        sx={{ borderRadius: 5, px: { xs: 3, md: 6 }, py: { xs: 4.5, md: 7 } }}
      >
        <Typography
          variant="h2"
          sx={{
            mx: 'auto',
            maxWidth: 780,
            fontWeight: 600,
            lineHeight: 1.5,
            color: 'common.white',
          }}
        >
          We hope your vacation is filled with joy, laughter, and unforgettable
          memories — and we&apos;re so glad to play a small part in making your
          trip a little easier and more relaxed.
        </Typography>
      </CtaBanner>
    </Section>
  </StaticPageLayout>
);

export default AboutUs;
